import React, { useEffect, useState, useCallback } from "react";
import { Select, Input, TimePicker } from "antd";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import SmartIcon from "../../../../components/generic/genericIcon/SmartIcon";
import { getFullName } from "../../../careprovider/visits/utils";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import CustomModal from "../../../../components/generic/custommodal/CustomModal";
import WorkforceSuggestionWrapper from "./WorkforceSuggestionWrapper";
import PrimaryButton from "../../../../components/generic/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/generic/buttons/SecondaryButton";

const slotDurationOptions = [
    { label: "30 minutes", value: 0.5 },
    { label: "1 hour", value: 1 },
    { label: "2 hours", value: 2 },
    { label: "3 hours", value: 3 },
    { label: "4 hours", value: 4 },
];

const SlotBasedFrequencyControl = ({
    setCarePlan,
    carePlan,
    readOnly,
    visitStartDate,
    patientName,
    typesOfServicesSelected,
    onConfirmShifts,
    confirmShiftsLoading,
}) => {
    const user = useReadMemory(MemoryType.USER);
    const isVolunteer = user?.jobTitle === "Volunteer";
    const [totalDuration, setTotalDuration] = useState(2); // in hours
    const [slotDuration, setSlotDuration] = useState(1); // in hours
    const [startTime, setStartTime] = useState(null);
    const [slots, setSlots] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [slotAssignModalOpen, setSlotAssignModalOpen] = useState(false);
    const [slotToAssign, setSlotToAssign] = useState(null);
    const [slotAssignBroadcastData, setSlotAssignBroadcastData] = useState({});
    // Shifts claimed in this session and not confirmed yet, so a volunteer can undo a mis-click
    const [pickedUpSlotIds, setPickedUpSlotIds] = useState([]);

    // Generate slots based on total duration and slot duration
    const generateSlots = useCallback(() => {
        if (!totalDuration || !slotDuration || totalDuration <= 0 || slotDuration <= 0) {
            setSlots([]);
            return;
        }

        setSlots(prevSlots => {
            const numberOfSlots = Math.ceil(totalDuration / slotDuration);
            const generatedSlots = [];

            // Base start time calculation
            const baseStart = startTime ? moment(startTime) : null;

            for (let i = 0; i < numberOfSlots; i++) {
                const slotStartOffset = i * slotDuration * 60; // minutes
                const slotEndOffset = Math.min((i + 1) * slotDuration, totalDuration) * 60; // minutes

                let slotStartTimeStr = null;
                let slotEndTimeStr = null;
                let displayTimeStr = null;
                let startTimeTimestamp = null;
                let endTimeTimestamp = null;

                if (baseStart) {
                    const slotStartTime = moment(baseStart).add(slotStartOffset, 'minutes');
                    const slotEndTime = moment(baseStart).add(slotEndOffset, 'minutes');
                    slotStartTimeStr = slotStartTime.format("HH:mm");
                    slotEndTimeStr = slotEndTime.format("HH:mm");
                    displayTimeStr = `${slotStartTime.format("h:mm A")} - ${slotEndTime.format("h:mm A")}`;

                    startTimeTimestamp = slotStartTime.valueOf();
                    endTimeTimestamp = slotEndTime.valueOf();
                }

                // Check if we can reuse existing slot data (id, assignments)
                // We reuse if the index matches. 
                // Note: If parameters changed significantly, assignments might not make sense, 
                // but for "initial load" consistency, this is key.
                const existingSlot = prevSlots[i];
                const reuseSlot = existingSlot && existingSlot.slotNumber === (i + 1);

                generatedSlots.push({
                    id: reuseSlot ? existingSlot.id : uuidv4(),
                    slotNumber: i + 1,
                    startHour: i * slotDuration,
                    endHour: Math.min((i + 1) * slotDuration, totalDuration),
                    startTime: slotStartTimeStr,
                    endTime: slotEndTimeStr,
                    displayTime: displayTimeStr,
                    startTimeTimestamp: startTimeTimestamp,
                    endTimeTimestamp: endTimeTimestamp,
                    duration: (slotEndOffset - slotStartOffset) / 60,
                    assignedVolunteer: reuseSlot ? existingSlot.assignedVolunteer : null,
                });
            }
            // Only update if actually different to avoid cycles? 
            // JSON.stringify comparison is heavy but safe. 
            // Or just set it. Since this runs on valid dependencies change, it should be fine.
            return generatedSlots;
        });
    }, [totalDuration, slotDuration, startTime]);

    // Remove volunteer from a slot
    const handleRemoveVolunteer = (slotId) => {
        const updatedSlots = slots.map((slot) =>
            slot.id === slotId ? { ...slot, assignedVolunteer: null } : slot
        );
        setSlots(updatedSlots);
    };

    // Open modal to assign volunteer to a slot
    const handleAssignSlotClick = (slotId) => {
        setSlotToAssign(slotId);
        setSlotAssignBroadcastData({});
        setSlotAssignModalOpen(true);
    };

    const getSlotAssignedVolunteer = (user) => {
        if (!user) return null;
        return {
            givenName: user?.givenName,
            familyName: user?.familyName,
            email: user?.email,
            gender: user?.gender,
            jobTitle: user?.jobTitle,
            telephone: user?.telephone,
            birthDate: user?.birthDate,
            userIdentifier: user?.userIdentifier,
            latitude: user?.latitude,
            longitude: user?.longitude,
            organizationIdentifier: user?.organizationIdentifier,
        };
    };

    // Volunteer claims an empty shift for themselves; saving is handled by the parent's confirm action
    const handleVolunteerPickUp = (slotId) => {
        setSlots((prev) =>
            prev.map((slot) =>
                slot.id === slotId && !slot.assignedVolunteer
                    ? { ...slot, assignedVolunteer: getSlotAssignedVolunteer(user) }
                    : slot
            )
        );
        setPickedUpSlotIds((prev) => (prev.includes(slotId) ? prev : [...prev, slotId]));
    };

    // Save the picked up shifts; the care plan update itself is owned by the parent
    const handleConfirmShifts = () => {
        if (!pickedUpSlotIds.length) return;
        onConfirmShifts?.();
    };

    const handleUndoPickUp = (slotId) => {
        setSlots((prev) =>
            prev.map((slot) =>
                slot.id === slotId ? { ...slot, assignedVolunteer: null } : slot
            )
        );
        setPickedUpSlotIds((prev) => prev.filter((id) => id !== slotId));
    };

    // Assign selected volunteer to the slot and close modal (one volunteer per slot only)
    const handleSlotAssignConfirm = () => {
        const selectedUsers = slotAssignBroadcastData?.selectedUsers || [];
        const selectedUser = selectedUsers.length === 1 ? selectedUsers[0] : null;
        if (slotToAssign && selectedUser) {
            const assignedVolunteer = getSlotAssignedVolunteer(selectedUser);
            // Replace previous assignment cleanly (one volunteer per slot)
            setSlots((prev) =>
                prev.map((slot) =>
                    slot.id === slotToAssign
                        ? { ...slot, assignedVolunteer }
                        : slot
                )
            );
        }
        setSlotAssignModalOpen(false);
        setSlotToAssign(null);
        setSlotAssignBroadcastData({});
    };

    // Update care plan with slot data
    useEffect(() => {
        let startTimeTimestamp = null;
        if (startTime) {
            if (visitStartDate) {
                // Combine visitStartDate date with startTime time
                const datePart = moment(visitStartDate).format("YYYY-MM-DD");
                const timePart = startTime.format("HH:mm:ss");
                startTimeTimestamp = moment(`${datePart}T${timePart}`).valueOf();
            } else {
                // Fallback
                startTimeTimestamp = startTime.valueOf();
            }
        }

        const slotData = {
            totalDuration,
            slotDuration,
            startTime: startTimeTimestamp,
            slots: slots.map((slot) => ({
                id: slot.id,
                slotNumber: slot.slotNumber,
                duration: slot.duration,
                startTime: slot.startTime,
                endTime: slot.endTime,
                startTimeTimestamp: slot.startTimeTimestamp,
                endTimeTimestamp: slot.endTimeTimestamp,
                assignedVolunteer: slot.assignedVolunteer,
            })),
        };

        setCarePlan((prev) => ({ ...prev, frequency: [slotData] }));

    }, [totalDuration, slotDuration, startTime, slots, setCarePlan, visitStartDate]);

    // Load existing slot data from carePlan (only once on initial mount)
    useEffect(() => {
        if (isInitialLoad) {
            if (carePlan?.frequencyPeriod === "slot based" && carePlan?.frequency?.[0]) {
                const slotData = carePlan.frequency[0];
                if (slotData.totalDuration) {
                    setTotalDuration(slotData.totalDuration);
                }
                if (slotData.slotDuration) {
                    setSlotDuration(slotData.slotDuration);
                }
                if (slotData.startTime) {
                    // Check if it's a timestamp (number or string number) or HH:mm string (legacy)
                    const isTimestamp = !isNaN(slotData.startTime) || (typeof slotData.startTime === 'string' && !slotData.startTime.includes(':'));

                    if (isTimestamp) {
                        setStartTime(moment(Number(slotData.startTime)));
                    } else {
                        setStartTime(moment(slotData.startTime, "HH:mm"));
                    }
                }

                if (slotData.slots && slotData.slots.length > 0) {
                    const loadedSlots = slotData.slots.map(s => ({
                        ...s,
                        // Re-calculate display time if missing?
                        displayTime: s.startTime && s.endTime ? `${moment(s.startTime, "HH:mm").format("h:mm A")} - ${moment(s.endTime, "HH:mm").format("h:mm A")}` : ""
                    }));
                    setSlots(loadedSlots);
                }
            }
            // Always set to false after first check
            setIsInitialLoad(false);
        }
    }, [carePlan?.frequencyPeriod, carePlan?.frequency, isInitialLoad]);

    useEffect(() => {
        if (!isInitialLoad && totalDuration && slotDuration && startTime) {
            generateSlots();
        }
    }, [totalDuration, slotDuration, startTime, isInitialLoad, generateSlots]);

      const footerButtons = (
        <>
          <SecondaryButton
            buttonText="Cancel"
            handleOnClick={() => {
              setSlotAssignModalOpen(false);
              setSlotToAssign(null);
              setSlotAssignBroadcastData({});
            }}
          />
          <PrimaryButton
            buttonText="Assign"
            handleOnClick={handleSlotAssignConfirm}
          />
        </>
      );

    return (
        <div className="slot-based-control">
            <div className="mt-2 d-flex align-items-center gap-3" style={{ flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '150px' }}>
                    <span>Start Time</span>
                    <div className="mt-2">
                        <TimePicker
                            value={startTime}
                            onChange={(time) => setStartTime(time)}
                            onSelect={(time) => setStartTime(time)}
                            placeholder="hh:mm"
                            format="hh:mm A"
                            minuteStep={15}
                            use12Hours
                            disabled={readOnly}
                            style={{ width: "100%" }}
                            popupClassName="time-picker-custom-class"
                            allowClear={false}
                        />
                    </div>
                </div>

                <div style={{ flex: 1, minWidth: '150px' }}>
                    <span>Total Duration (hours)</span>
                    <div className="mt-2">
                        <Input
                            type="number"
                            min={1}
                            max={24}
                            value={totalDuration}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                    setTotalDuration(value === '' ? 0 : parseInt(value, 10));
                                }
                            }}
                            disabled={readOnly}
                            style={{ width: "100%" }}
                            placeholder="Enter hours"
                        />
                    </div>
                </div>

                <div style={{ flex: 1, minWidth: '150px' }}>
                    <span>Shift Duration</span>
                    <div className="mt-2">
                        <Select
                            value={slotDuration}
                            onChange={(value) => setSlotDuration(value)}
                            options={slotDurationOptions}
                            disabled={readOnly}
                            style={{ width: "100%" }}
                        />
                    </div>
                </div>
            </div>

            {(() => {
                const visibleSlots = isVolunteer
                    ? slots.filter(slot => slot?.assignedVolunteer?.userIdentifier === user?.userIdentifier || !slot?.assignedVolunteer)
                    : slots;

                if (visibleSlots?.length === 0) return null;

                return (
                    <div className="mt-3">

                        <div
                            className="slots-container"
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                                gap: "8px",
                            }}
                        >
                            {visibleSlots.map((slot) => {
                                // Volunteers can only undo a pick-up they made before confirming it
                                const isUnconfirmedPickUp = isVolunteer && pickedUpSlotIds.includes(slot.id);
                                const canClearSlot = isVolunteer ? isUnconfirmedPickUp : !readOnly;

                                return (
                                <div
                                    key={slot.id}
                                    className="slot-item"
                                    style={{
                                        border: "1px solid var(--cards-border-color)",
                                        borderRadius: 4,
                                        backgroundColor: slot.assignedVolunteer ? "var(--selected-bg-color)" : "#fff",
                                        padding: "4px 8px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 2,
                                        fontSize: "12px"
                                    }}
                                >
                                    <div>
                                        <strong style={{ color: "var(--primary-text-color)" }}>Shift {slot.slotNumber}</strong>
                                    </div>

                                    <div style={{ color: "var(--secondary-text-color)" }}>
                                        {slot.displayTime || `${slot.duration} ${slot.duration === 1 ? "hour" : "hours"}`}
                                    </div>

                                    <div style={{ marginTop: "auto" }}>
                                        {slot.assignedVolunteer ? (
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <strong style={{ color: "var(--primary-text-color)", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}>
                                                    {getFullName(slot?.assignedVolunteer?.givenName, slot?.assignedVolunteer?.familyName)}
                                                </strong>
                                                {canClearSlot && (
                                                    <SmartIcon
                                                        iconKey="reject"
                                                        handleIconClick={() =>
                                                            isVolunteer
                                                                ? handleUndoPickUp(slot.id)
                                                                : handleRemoveVolunteer(slot.id)
                                                        }
                                                        width={20}
                                                        height={20}
                                                    />
                                                )}
                                            </div>
                                        ) : isVolunteer ? (
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleVolunteerPickUp(slot.id)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        handleVolunteerPickUp(slot.id);
                                                    }
                                                }}
                                                style={{
                                                    color: "var(--main-theme-color)",
                                                    cursor: "pointer",
                                                    fontWeight: 500,
                                                }}
                                            >
                                                Pick Up
                                            </span>
                                        ) : (
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <span style={{ color: "var(--text-warning)" }}>Not Assigned</span>
                                                {!readOnly && (
                                                    <SmartIcon
                                                        iconKey="volunteerRequest"
                                                        handleIconClick={() => handleAssignSlotClick(slot.id)}
                                                        width={18}
                                                        height={18}
                                                        color="var(--main-theme-color)"
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            {isVolunteer && onConfirmShifts && (
                <div className="submit-btn mt-2">
                    <PrimaryButton
                        buttonText="Confirm Shifts"
                        handleOnClick={handleConfirmShifts}
                        buttonDisable={!pickedUpSlotIds.length}
                        loading={confirmShiftsLoading}
                        tooltipTitle={!pickedUpSlotIds.length && "Pick up a shift to continue!"}
                    />
                </div>
            )}

            {slotAssignModalOpen && (
                <CustomModal
                    closable={true}
                    isModalOpen={slotAssignModalOpen}
                    footer={[footerButtons]}
                    closeModal={(open) => {
                        if (!open) {
                            setSlotAssignModalOpen(false);
                            setSlotToAssign(null);
                            setSlotAssignBroadcastData({});
                        }
                    }}
                    title="Assign volunteer to this Shift"
                >
                    <WorkforceSuggestionWrapper
                        setBroadcastData={setSlotAssignBroadcastData}
                        typesOfServicesSelected={typesOfServicesSelected}
                        patientName={patientName}
                        opportunities={[]}
                        filterRequired={true}
                        selectedFilters={slotAssignBroadcastData?.selectedFilters}
                    />
                </CustomModal>
            )}
        </div>
    );
};

export default SlotBasedFrequencyControl;
